import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import React, { memo, useCallback } from 'react'
import { fullScreenModeTooltipTitle } from '../../KeyboardShortcuts/FullScreenModeTooltipTitle'
import FullScreenIcon from '../../../assets/icons/FullScreenIcon'
import { OPTION_CHAIN_TABLE_ID, OptionChainViewKey } from '../../../assets/constants'
import OptionChainTimeIntervalMenu from './optionChainTimeIntervalMenu'
import OptionChainQuickViewMenu from './optionChainQuickViewMenu'
import { useStyles } from './styles'
import OptionChainSettings from './optionChainSettings'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import commonActions from '../../../actions/common.actions'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { selectOptionChainVisibleStrikes, selectShowOptionChainQuantityInLots } from '../../../selectors/common'
import { strikeValues } from '../../../util/tradePage'

const OptionChainHeader: React.FC<{ showFullScreen?: boolean, orderOptionChain?: boolean, optionChainViewKey?: OptionChainViewKey }> = ({
    showFullScreen = true,
    orderOptionChain = false,
    optionChainViewKey
}) => {
    const dispatch = useDispatch()
    const visibleStrikes = useSelector(selectOptionChainVisibleStrikes)
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const handleOptionChainFullScreen = useCallback(() => {
        AnalyticsManager.logEvent("Full Screen Clicked", { source: 'option-chain-bar' })
        const tableElement = document.getElementById(OPTION_CHAIN_TABLE_ID)
        if (tableElement && !document.fullscreenElement) {
            tableElement.requestFullscreen()
        }
    }, [])
    const { classes } = useStyles()
    const handleStrikeChange = useCallback((value: string) => {
        commonActions.setOptionChainVisibleStrikes(dispatch, value)
    }, [dispatch])

    return (
        <Box sx={{
            padding: orderOptionChain ? '2px 4px' : 0,
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            flexDirection: 'row'
        }}>
            <OptionChainQuickViewMenu orderOptionChain={orderOptionChain} optionChainViewKey={optionChainViewKey} />
            <OptionChainTimeIntervalMenu orderOptionChain={orderOptionChain} optionChainViewKey={optionChainViewKey} />
            {
                showFullScreen ? (
                    <Tooltip title={fullScreenModeTooltipTitle} arrow placement="bottom">
                        <ExtraSmallButton
                            onClick={handleOptionChainFullScreen}
                            className={classes.item}
                            color="inherit"
                            data-event="Full screen clicked"
                            data-event-source="option-chain-bar"
                        >
                            <FullScreenIcon fontSize="small" />
                        </ExtraSmallButton>
                    </Tooltip>
                ) : null
            }
            {
                orderOptionChain ?
                    <NonInteractiveTooltip title="No of Strikes" arrow placement="top">
                        <ToggleButtonGroup
                            size="small"
                            value={visibleStrikes}
                            aria-label="text alignment"
                            sx={{
                                ml: 1,
                                height: "32px",
                                '.MuiButtonBase-root': {
                                    borderColor: (theme) => theme.palette.divider
                                },
                            }}
                        >{
                                strikeValues.values.map((s) => {
                                    return (<ToggleButton key={s.value} aria-label="left aligned" selected={s.value == visibleStrikes} value={s.value} onClick={() => handleStrikeChange(s.value)}
                                        sx={{ minWidth: "40px" }}>
                                        {s.label}
                                    </ToggleButton>)
                                })
                            }
                        </ToggleButtonGroup>
                    </NonInteractiveTooltip> : null
            }
            {
                orderOptionChain ?
                    <NonInteractiveTooltip title="Quantity in Lots" arrow placement="top">
                        <ToggleButtonGroup
                            size="small"
                            value={quantityInLots ? 'Yes' : 'No'}
                            aria-label="text alignment"
                            sx={{
                                ml: 1,
                                height: "32px",
                                '.MuiButtonBase-root': {
                                    borderColor: (theme) => theme.palette.background.default
                                },
                            }}
                        ><ToggleButton
                            key="Yes"
                            value="Yes"
                            selected={quantityInLots}
                            sx={{ width: '50px', }}
                            onChange={() => {
                                AnalyticsManager.logEvent('Quantity in lots', {
                                    source: 'option-chain-bar',
                                    value: !quantityInLots
                                })
                                commonActions.setOptionChainQuantityInLots(dispatch, !quantityInLots)
                            }}
                        >
                                Yes
                            </ToggleButton>
                            <ToggleButton
                                key="No"
                                value="No"
                                selected={!quantityInLots}
                                sx={{ width: '50px', }}
                                onChange={() => {
                                    AnalyticsManager.logEvent('Quantity in lots', {
                                        source: 'option-chain-bar',
                                        value: !quantityInLots
                                    })
                                    commonActions.setOptionChainQuantityInLots(dispatch, !quantityInLots)
                                }}
                            >
                                No
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </NonInteractiveTooltip> : null
            }
            {
                !orderOptionChain ?
                    <OptionChainSettings orderOptionChain={orderOptionChain} optionChainViewKey={optionChainViewKey} /> : null
            }
        </Box>
    )
}

export default memo(OptionChainHeader)
